import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useRouter } from 'next/router'

import { useLoaderStore } from '@/store/loader'

import { MaskTransition, MaskTransitionHandler } from './components'
import { useTransitionStore } from './use-transition-store'

export const TransitionContext = createContext<undefined>(undefined)

const TransitionProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter()
  const active = useRef(false)
  const nodeRef = useRef<HTMLElement>(null!)
  const maskRef = useRef<MaskTransitionHandler>(null!)
  const { contextSafe } = useGSAP({ scope: nodeRef })
  const setLoaderState = useLoaderStore((state) => state.setState)
  const setTransitionState = useTransitionStore((state) => state.setState)

  const handleLeaveTransition = contextSafe(() => {
    active.current = false
    setLoaderState({ pageLoaded: false })
    setTransitionState({ active: true })
    document.documentElement.classList.add('page-transition')
  })

  const handleEnterTransition = contextSafe(() => {
    document.documentElement.classList.remove('page-transition')
    active.current = true
  })

  const handleEndListener = contextSafe(async (done: () => void) => {
    if (!active.current) {
      await maskRef.current.enterTimeline()
      done()
    } else if (isLoaded()) {
      setTransitionState({ reveal: true })
      await maskRef.current.leaveTimeline()
      setTransitionState({ active: false, reveal: false })
      done()
    } else {
      console.log('Waiting for loader state...')
      maskRef.current.resetTimeline()
      await loaderStateWatcher()
      handleEndListener(done)
    }
  })

  const isLoaded = useCallback(
    () => useLoaderStore.getState().pageLoaded && document.fonts.status === 'loaded',
    []
  )

  const loaderStateWatcher = () =>
    new Promise<void>((resolve) => {
      const tick = () => {
        if (isLoaded()) {
          gsap.ticker.remove(tick)
          resolve()
        }
      }
      gsap.ticker.add(tick)
    })

  const contextValue = useMemo(() => undefined, [])

  useEffect(() => {
    ScrollTrigger.clearScrollMemory('manual')

    router.beforePopState((state) => {
      state.options.scroll = false
      return true
    })
  }, [router])

  return (
    <TransitionContext.Provider value={contextValue}>
      <SwitchTransition mode="out-in">
        <Transition
          key={router.asPath}
          nodeRef={nodeRef}
          addEndListener={handleEndListener}
          onEnter={handleEnterTransition}
          onExit={handleLeaveTransition}
          unmountOnExit
        >
          <main ref={nodeRef} className="static h-full w-full">
            {children}
            <MaskTransition ref={maskRef} />
          </main>
        </Transition>
      </SwitchTransition>
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
